import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { SelectorInputProps } from "./types";
import { Column, Row } from "../../containers";
import { AlignItems, JustifyContent } from "../../containers/types";
import { useThemeStore } from "../../../stores";
import { Heading, Text } from "../../typography";
import { CheckIcon, CloseIcon } from "../../../assets/icons";

const SelectorInput = <T,>({
  selected,
  setSelected,
  items,
  compareFn,
  stringField,
  uniqueKey,
  renderStrings,
  label,
  props,
  className = "",
  style = {},
  color,
  background,
  inputProps,
  readOnly = false,
}: SelectorInputProps<T>) => {
  const theme = useThemeStore((s) => s.theme);
  const ref: RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);
  const [isFocus, setIsFocus] = useState<boolean>(false);
  const [selectedIdx, setSelectedIdx] = useState<number>(0);

  const handler = useCallback(
    (e: MouseEvent): void => {
      if ((e.target as Element).tagName.toLowerCase() !== "input") {
        ref.current?.blur();
      }
    },
    [ref]
  );

  useEffect(() => {
    window.addEventListener("click", handler);
    return (): void => {
      window.removeEventListener("click", handler);
    };
  }, [handler]);

  const [textValue, setTextValue] = useState<string>("");
  const [foundItems, setFoundItems] = useState<Array<T>>([]);

  useEffect(() => {
    if (!isFocus || readOnly) {
      setTimeout(() => {
        setFoundItems([]);
      }, 50);
      return;
    }
    setFoundItems(items.filter((c) => compareFn(c, textValue)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textValue, items, isFocus]);

  useEffect(() => {
    if (isFocus && !readOnly) {
      setFoundItems(items);
    }
  }, [isFocus, items, readOnly]);

  useEffect(() => {
    if (!selected) return;
    setTextValue(selected[stringField] as string);
  }, [selected, stringField]);

  useEffect(() => {
    if (
      selected &&
      (selected[stringField] as string).toLowerCase() !==
        textValue.toLowerCase()
    )
      setSelected(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textValue]);

  useEffect(() => {
    setSelectedIdx(0);
  }, [foundItems]);

  const onUp = useCallback(() => {
    if (selectedIdx === 0) return;
    setSelectedIdx((prev) => prev - 1);
  }, [selectedIdx]);

  const onDown = useCallback(() => {
    if (selectedIdx === foundItems.length - 1) return;
    setSelectedIdx((prev) => prev + 1);
  }, [foundItems.length, selectedIdx]);

  const onSelect = useCallback(() => {
    setSelected(foundItems[selectedIdx]);
    ref.current?.blur();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foundItems, selectedIdx, ref]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          onDown();
          break;
        case "ArrowUp":
          onUp();
          break;
        case "Enter":
          onSelect();
          break;
        default:
          return;
      }
    },
    [onDown, onSelect, onUp]
  );

  useEffect(() => {
    if (!isFocus) return;
    window.addEventListener("keydown", onKeyDown);
    return (): void => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onKeyDown, isFocus]);

  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    itemsRef.current[selectedIdx]?.scrollIntoView({ behavior: "smooth" });
  }, [selectedIdx]);

  return (
    <Column
      alignItems={AlignItems.Start}
      justifyContent={JustifyContent.Start}
      gap={5}
      className={className}
      style={{ width: "100%", position: "relative", ...style }}
      props={props}
    >
      <Row gap={10}>
        <Heading color={color || theme.textOnOverlay} size={16}>
          {label}
        </Heading>
        {selected ? (
          <CheckIcon color={theme.primary} />
        ) : (
          <CloseIcon color={theme.alert} />
        )}
      </Row>
      <input
        ref={ref}
        value={textValue}
        onChange={(e) => setTextValue(e.target.value)}
        style={{
          borderRadius: !foundItems.length ? 15 : "15px 15px 0 0",
          background: background || theme.background,
          width: "calc(100% - 10px)",
          height: 41,
          paddingRight: 10,
          color:
            background === theme.background
              ? theme.textOnBg
              : theme.textOnOverlay,
        }}
        onFocus={() => setIsFocus(true)}
        onBlur={() => setIsFocus(false)}
        readOnly={readOnly}
        {...inputProps}
      />
      <Column
        style={{
          position: "absolute",
          top: "100%",
          left: 0,
          width: "100%",
          borderRadius: "0 0 15px 15px",
          background: background || theme.background,
          boxShadow: `0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)`,
          maxHeight: 200,
          overflowY: "scroll",
          zIndex: 99,
        }}
      >
        {foundItems.map((c, idx) => (
          <div
            key={c[uniqueKey] as string}
            style={{
              padding: "0.5rem",
              width: "calc(100% - 1rem)",
              cursor: "pointer",
              background: idx === selectedIdx ? theme.primary : "transparent",
            }}
            className="row align-items-center justify-content-evenly"
            onClick={() => setSelected(c)}
            ref={(ref) => {
              if (!idx) itemsRef.current = [];
              itemsRef.current.push(ref);
            }}
          >
            {renderStrings.map((rsk) => (
              <Text
                key={rsk as string}
                color={
                  idx === selectedIdx
                    ? theme.textOnprimary
                    : background === theme.background
                    ? theme.textOnBg
                    : theme.textOnOverlay
                }
              >
                {c[rsk] as string}
              </Text>
            ))}
          </div>
        ))}
      </Column>
    </Column>
  );
};

export default SelectorInput;
